/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { FC } from 'react'
import { Routes, Route, BrowserRouter, Navigate } from 'react-router-dom'
import { ErrorsPage } from '../modules/errors/ErrorsPage'
import { Logout, AuthPage } from '../modules/auth'
import { App } from '../App'
import { TpvAdminDashboardWrapper } from '../pages/TPVAdmin/DashboardWrapper'
import ClientList from '../pages/TPVAdmin/ClientList'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import { AgentDashboard } from '../pages/AgentAdmin/dashboard'
import { TeamAdminDashboard } from '../pages/TeamAdmin/TeamAdminDashboard'
import { SalesAdminDashboard } from '../pages/SalesAdmin/Dashboard/SalesAdminDashboard'
import QcUserList from '../pages/TeamAdmin/QcUser/QcUserList'
import { SalesChannelList } from '../pages/TeamAdmin/SalesChannels/SalesChannelList'
import { TeamAdminProducts } from '../pages/TeamAdmin/Products/TeamAdminProducts'
import AgentVerification from '../pages/TeamAdmin/AgentVerification/AgentVerification'
import TPVScript from '../pages/TeamAdmin/TPVScript/TPVScript'
import EditSalesChannel from '../pages/TeamAdmin/SalesChannels/EditSalesChannel/EditSalesChannel'
import { AgentList } from '../pages/SalesAdmin/AgentList'
import QCDashboard from '../pages/QCAdmin/Dashboard/QCDashboard'
import TPVRecordingList from '../pages/SharedPages/TPVRecordingList/TPVRecordingList'
import CustomerDetails from '../pages/SharedPages/TPVRecordingList/CustomerDetails/CustomerDetails'
import UserProfile from '../sharedComponent/UserProfile/UserProfile'
import ScheduledReports from '../pages/SharedPages/TPVRecordingList/ScheduledReports/ScheduledReports'
import CustomerBill from '../pages/CustomerBill/CustomerBill'
import LiveAgents from '../pages/TPVAdmin/LiveAgentsList/LiveAgents'
import LiveAgentsCall from '../pages/LiveAgents/LiveAgentsCall'
import { Vendors } from '../pages/TPVAdmin/VendorsList'
import ReInitiatePage from '../pages/TPVAdmin/Re-Initiate'
import SmartDocs from '../pages/TeamAdmin/smartDocs/smartDocslist/smartDocs'
import SMSTpv from '../pages/SMSTpv'
import WelcomeMessage from '../pages/SMSTpv/welcomeSms'
import Contract from '../pages/Contract'
import EnrollmentDocuments from '../pages/EnrollmentDocuments'
import DoNotEnrollList from '../pages/TeamAdmin/DoNotEnroll/DoNotEnrollList'
import ReusableAuditTrail from '../pages/AuditTrail/AuditTrail'
import OutboundLiveAgentCallList from '../pages/LiveAgents/OutboundLiveAgent'
import OutboundLiveAgentCall from '../pages/LiveAgents/OutboundLiveAgent/OutboundLiveAgentCall'
import AgentLeaderList from '../pages/SharedPages/AgentLeaderList'
import CustomerList from '../pages/LeadGen/CustomerList'
import CustomerScript from '../pages/LeadGen/CustomerScript'
import CustomerReport from '../pages/AgentAdmin/CustomerReport'
import CustomerImage from '../pages/CustomerImageUpload/customerImageUpload'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env
const loggedIn = localStorage.getItem('_accesstoken')
const AppRoutes: FC = () => {
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route path='error/*' element={<ErrorsPage />} />
          <Route path=':adminUser/logout' element={<Logout />} />
          <Route path=':adminUser/profile' element={<MasterLayout component={UserProfile} />} />
          {/* Tpv routes */}
          <Route
            path=':adminUser/tpv-dashboard'
            element={<MasterLayout component={TpvAdminDashboardWrapper} />}
          />
          <Route
            path=':adminUser/tpv-client-list'
            element={<MasterLayout component={ClientList} />}
          />
          <Route
            path=':adminUser/live-agents-list'
            element={<MasterLayout component={LiveAgents} />}
          />
          {loggedIn ? (
            <Route path='/tpvadmin' element={<Navigate to='/tpvadmin/tpv-dashboard' replace />} />
          ) : (
            <Route path='/tpvadmin' element={<Navigate to='/tpvadmin/login' replace />} />
          )}
          {/* Agent routes */}
          <Route
            path=':adminUser/agent-dashboard'
            element={<MasterLayout component={AgentDashboard} />}
          />
          <Route
            path=':adminUser/agent-report'
            element={<MasterLayout component={CustomerReport} />}
          />
          {loggedIn ? (
            <Route path='/agent' element={<Navigate to='/agent/agent-dashboard' replace />} />
          ) : (
            <Route path='/agent' element={<Navigate to='/agent/login' replace />} />
          )}
          {/* Team Admin Routes */}
          <Route
            path=':adminUser/dashboard'
            element={<MasterLayout component={TeamAdminDashboard} />}
          />
          <Route
            path=':adminUser/customer-list'
            element={<MasterLayout component={CustomerList} />}
          />
          <Route path=':adminUser/smart-docs' element={<MasterLayout component={SmartDocs} />} />
          <Route
            path=':adminUser/customer-script'
            element={<MasterLayout component={CustomerScript} />}
          />
          <Route path=':adminUser/qc-list' element={<MasterLayout component={QcUserList} />} />
          <Route
            path=':adminUser/sales-channel-list'
            element={<MasterLayout component={SalesChannelList} />}
          />
          <Route
            path=':adminUser/products'
            element={<MasterLayout component={TeamAdminProducts} />}
          />
          <Route path=':adminUser/tpvscript' element={<MasterLayout component={TPVScript} />} />
          <Route
            path=':adminUser/sales-channel-list/edit-salesChannel'
            element={<MasterLayout component={EditSalesChannel} />}
          />
          <Route
            path=':adminUser/do-not-enroll'
            element={<MasterLayout component={DoNotEnrollList} />}
          />
          <Route
            path=':adminUser/tpv-report'
            element={<MasterLayout component={TPVRecordingList} />}
          />
          <Route
            path=':adminUser/tpv-report/customer-details'
            element={<MasterLayout component={CustomerDetails} />}
          />
          <Route
            path=':adminUser/tpv-report/scheduled-reports'
            element={<MasterLayout component={ScheduledReports} />}
          />
          {loggedIn ? (
            <Route path='/systemadmin' element={<Navigate to='/systemadmin/dashboard' replace />} />
          ) : (
            <Route path='/systemadmin' element={<Navigate to='/systemadmin/login' replace />} />
          )}
          -{/* Sales Admin Routes */}
          <Route
            path=':adminUser/sales-dashboard'
            element={<MasterLayout component={SalesAdminDashboard} />}
          />
          <Route
            path=':adminUser/sales-agent-list'
            element={<MasterLayout component={AgentList} />}
          />
          <Route
            path=':adminUser/sales-report'
            element={<MasterLayout component={CustomerReport} />}
          />
          {loggedIn ? (
            <Route
              path='/salesadmin'
              element={<Navigate to='/salesadmin/sales-dashboard' replace />}
            />
          ) : (
            <Route path='/salesadmin' element={<Navigate to='/salesadmin/login' replace />} />
          )}
          {/* QC Admin */}
          <Route
            path=':adminUser/qc-dashboard'
            element={<MasterLayout component={QCDashboard} />}
          />
          <Route
            path=':adminUser/tpv-recording-list'
            element={<MasterLayout component={TPVRecordingList} />}
          />
          <Route
            path=':adminUser/qc-dashboard/customer-details'
            element={<MasterLayout component={CustomerDetails} />}
          />
          <Route
            path=':adminUser/tpv-recording-list/customer-details'
            element={<MasterLayout component={CustomerDetails} />}
          />
          <Route
            path=':adminUser/qc-dashboard/scheduled-reports'
            element={<MasterLayout component={ScheduledReports} />}
          />
          <Route
            path=':adminUser/tpv-recording-list/scheduled-reports'
            element={<MasterLayout component={ScheduledReports} />}
          />
          <Route
            path=':adminUser/agent-verification'
            element={<MasterLayout component={AgentVerification} />}
          />
          {loggedIn ? (
            <Route path='/qcuser' element={<Navigate to='/qcuser/qc-dashboard' replace />} />
          ) : (
            <Route path='/qcuser' element={<Navigate to='/qcuser/login' replace />} />
          )}
          <Route path=':adminUser/*' element={<AuthPage />} />
          <Route path='customerbill' element={<CustomerBill />} />
          <Route path='customerdocumentupload' element={<CustomerImage />} />
          <Route index element={<Navigate to='tpvadmin/login' />} />
          {/* // Live Agent Call */}
          <Route
            path=':adminUser/liveagent-call'
            element={<MasterLayout component={LiveAgentsCall} />}
          />
          <Route
            path=':adminUser/outbound-liveagent'
            element={<MasterLayout component={OutboundLiveAgentCallList} />}
          />
          <Route
            path=':adminUser/outbound-liveagent-call'
            element={<MasterLayout component={OutboundLiveAgentCall} />}
          />
          <Route path='/liveagent' element={<Navigate to='/liveagent/login' replace />} />
          {/* Vendors */}
          <Route path=':adminUser/vendors-list' element={<MasterLayout component={Vendors} />} />
          <Route path=':adminUser/monitor' element={<MasterLayout component={ReInitiatePage} />} />

          <Route path='/sms-tpv' element={<SMSTpv />} />
          <Route path='/welcome-sms-tpv' element={<WelcomeMessage />} />
          <Route path='/contract' element={<Contract />} />
          <Route path='/enrollmentDocuments' element={<EnrollmentDocuments />} />
          {/*Audit */}
          <Route
            path=':adminUser/audit-trail'
            element={<MasterLayout component={ReusableAuditTrail} />}
          />
          <Route
            path=':adminUser/product-audit'
            element={<MasterLayout component={ReusableAuditTrail} />}
          />

          <Route
            path=':adminUser/script-audit'
            element={<MasterLayout component={ReusableAuditTrail} />}
          />
          <Route
            path=':adminUser/saleschannel-audit'
            element={<MasterLayout component={ReusableAuditTrail} />}
          />
          <Route
            path=':adminUser/saleschannelagent-audit'
            element={<MasterLayout component={ReusableAuditTrail} />}
          />
          {/* sharedRoutes */}
          <Route
            path=':adminUser/agent-leader-list'
            element={<MasterLayout component={AgentLeaderList} />}
          />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export { AppRoutes }
